import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useState } from 'react';

import { colours } from 'src/constants/colours';
import type { GeometryInput } from 'src/types/__generated__/graphql';
import { isPolygonSelfIntersecting } from 'src/utils/googleMapUtils';

type CoordinatesLngLat = number[];
type CoordinatesPoints = CoordinatesLngLat[];
type Coordinates = CoordinatesPoints[];

const drawPolygonDefaultOptions = {
    editable: true,
    strokeColor: colours.secondary.main,
    strokeOpacity: 1,
    strokeWeight: 2,
    fillColor: colours.secondary.main,
    fillOpacity: 0,
};

const overlayPolygonDefaultOptions = {
    strokeColor: colours.secondary.main,
    strokeOpacity: 1,
    strokeWeight: 2,
    fillColor: colours.black.main,
    fillOpacity: 0.2,
};

type Args = {
    map: google.maps.Map;
    setIsDrawingMode: (isDrawingMode: boolean) => void;
};

/**
 * Hook that manage the drawing of a polygon on the map.
 */
export const useMapDrawing = ({ map, setIsDrawingMode }: Args) => {
    const { t } = useTranslation(['ad']);
    const [drawingError, setDrawingError] = useState<string | undefined>(undefined);
    const [drawnPolygon, setDrawnPolygon] = useState<google.maps.Polygon | null>(null); // the polygon in drawing mode
    const [overlayPolygon, setOverlayPolygon] = useState<google.maps.Polygon | null>(null); // the highlighted polygon (no drawing mode)
    const [drawingManager, setDrawingManager] = useState<google.maps.drawing.DrawingManager | null>(null);
    const [isDrawnPolygonValid, setIsDrawnPolygonValid] = useState(false);

    const validateDrawnPolygon = useCallback(
        (polygon: google.maps.Polygon | null) => {
            if (!polygon) {
                setIsDrawnPolygonValid(false);
                setDrawingError(undefined);
                return;
            }

            const polygonPointsNumber = polygon.getPath().getArray().length;
            if (polygonPointsNumber < 3) {
                setIsDrawnPolygonValid(false);
                setDrawingError(t('ad:mapDrawing.mustHaveAtLeast3Points'));
                return;
            }

            const isSelfIntersecting = isPolygonSelfIntersecting(polygon);
            if (isSelfIntersecting) {
                setIsDrawnPolygonValid(false);
                setDrawingError(t('ad:mapDrawing.polygonLinesIntersect'));
                return;
            }

            setDrawingError(undefined);
            setIsDrawnPolygonValid(true);
        },
        [t],
    );

    // We listen the event 'set_at' and 'insert_at' to revalidate the drawn polygon if it changes
    useEffect(() => {
        let setAtListener: google.maps.MapsEventListener | undefined;
        let insertAtListener: google.maps.MapsEventListener | undefined;
        if (drawnPolygon) {
            setAtListener = google.maps.event.addListener(drawnPolygon.getPath(), 'set_at', () => {
                validateDrawnPolygon(drawnPolygon);
            });

            insertAtListener = google.maps.event.addListener(drawnPolygon.getPath(), 'insert_at', () => {
                validateDrawnPolygon(drawnPolygon);
            });
        }

        return () => {
            if (setAtListener) {
                google.maps.event.removeListener(setAtListener);
            }
            if (insertAtListener) {
                google.maps.event.removeListener(insertAtListener);
            }
        };
    }, [drawnPolygon, validateDrawnPolygon]);

    const updateDrawnPolygon = (polygon: google.maps.Polygon | null) => {
        setDrawnPolygon(polygon);
        validateDrawnPolygon(polygon);
    };

    const drawOverlayPolygonFromDrawnPolygon = (drawnPoly: google.maps.Polygon) => {
        const isCounterClockwise = google.maps.geometry.spherical.computeSignedArea(drawnPoly.getPath()) < 0;

        const worldPolygon = [
            new google.maps.LatLng(-85.1054596961173, -180),
            new google.maps.LatLng(85.1054596961173, -180),
            new google.maps.LatLng(85.1054596961173, 180),
            new google.maps.LatLng(-85.1054596961173, 180),
            new google.maps.LatLng(-85.1054596961173, 0),
        ];

        if (isCounterClockwise) {
            worldPolygon.reverse();
        }

        // Add a second polygon path in reverse order creates a hole in the polygon
        const paths = [worldPolygon, drawnPoly.getPath()];
        const overlayPolygonTemp = new google.maps.Polygon({
            paths,
            ...overlayPolygonDefaultOptions,
        });

        overlayPolygonTemp.setMap(map);
        setOverlayPolygon(overlayPolygonTemp);
    };

    const transformQueryVariablesPlaceToDrawnPolygon = (place: GeometryInput) => {
        const placeWoopenPolygon = place.coordinates as Coordinates;
        const paths = placeWoopenPolygon[0]
            .map(([lng, lat]) => Boolean(lat && lng) && new google.maps.LatLng({ lat, lng }))
            .filter(Boolean);
        const drawnPolygonTemp = new google.maps.Polygon({
            paths: paths as unknown as google.maps.LatLng[],
            ...drawPolygonDefaultOptions,
        });
        drawnPolygonTemp.setMap(null);
        updateDrawnPolygon(drawnPolygonTemp);
        return drawnPolygonTemp;
    };

    const clearCurrentPolygon = () => {
        drawnPolygon?.setPaths([]);
        updateDrawnPolygon(null);
        overlayPolygon?.setPaths([]);
        setOverlayPolygon(null);
    };

    const completePolygon = (polygon: google.maps.Polygon) => {
        updateDrawnPolygon(polygon);
        drawingManager?.setMap(null); // this allow to draw only 1 polygon, when the polygon is complete, we quit the drawing manager
    };

    const clearPolygonAndActivateDrawing = () => {
        // We clear the current polygon when we draw a new one
        clearCurrentPolygon();

        // We set the drawing manager to the map to be able to draw a polygon
        drawingManager?.setMap(map);
        drawingManager?.setDrawingMode(google.maps.drawing.OverlayType.POLYGON);
    };

    const clearDrawingWithoutExitDrawingMode = () => {
        clearCurrentPolygon();

        // We set the drawing manager to null and then we set it again to the map to reset the drawing mode
        drawingManager?.setMap(null);
        drawingManager?.setMap(map);
        drawingManager?.setDrawingMode(google.maps.drawing.OverlayType.POLYGON);
    };

    const exitDrawingMode = () => {
        setDrawingError(undefined);
        drawingManager?.setMap(null);
        setIsDrawingMode(false);
    };

    return {
        drawingError,
        drawnPolygon,
        isDrawnPolygonValid,
        drawingManager,
        setDrawingManager,
        drawOverlayPolygonFromDrawnPolygon,
        transformQueryVariablesPlaceToDrawnPolygon,
        clearCurrentPolygon,
        completePolygon,
        clearPolygonAndActivateDrawing,
        clearDrawingWithoutExitDrawingMode,
        exitDrawingMode,
    };
};
